import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { approachContent } from '../data/mock';
import AnimatedSection from '../components/common/AnimatedSection';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Eye, HelpCircle, Lightbulb, Zap, Check
} from 'lucide-react';

const ApprochePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = approachContent[language] || approachContent.fr;

  const verbIcons = {
    eye: Eye,
    'help-circle': HelpCircle,
    lightbulb: Lightbulb,
    play: Zap
  };

  const iconColors = ['bg-[#5B2D8E]', 'bg-[#7C4DAF]', 'bg-[#FFC107]', 'bg-[#0D9488]'];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className={`py-20 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#FAFAF9] to-white'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <span className="caption mb-4 block">Notre méthode</span>
              <h1 className="heading-1 mb-6">{content.title}</h1>
              <div className="divider mx-auto mb-8" />
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="body-large max-w-2xl mx-auto">
                {content.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 4 Verbes Section */}
      <section className={`section-lg ${isDark ? 'bg-[#0C0A09]' : 'bg-white'}`}>
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {content.verbs.map((verb, index) => {
              const Icon = verbIcons[verb.icon] || Eye;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div 
                    className="card-feature text-center group"
                    whileHover={{ y: -8 }}
                  >
                    <div className={`w-16 h-16 mx-auto mb-5 ${iconColors[index]} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-8 h-8 ${index === 2 ? 'text-[#1C1917]' : 'text-white'}`} />
                    </div>
                    <h3 className="heading-3 mb-3">{verb.word}</h3>
                    <p className="text-theme-secondary">{verb.description}</p>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Posture constructive section */}
      <section className={`section-lg ${isDark ? 'bg-[#1C1917]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading-2 mb-4">{content.constructive.title}</h2>
              <div className="divider mx-auto" />
            </AnimatedSection>

            <div className="space-y-4">
              {content.constructive.items.map((item, index) => (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div 
                    className={`flex items-center gap-4 rounded-2xl p-5 border ${
                      isDark 
                        ? 'bg-[#292524] border-white/10' 
                        : 'bg-white border-stone-100'
                    }`}
                    whileHover={{ x: 8 }}
                  >
                    <div className="w-10 h-10 bg-[#5B2D8E] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-lg font-medium text-theme-primary">
                      {item}
                    </p>
                  </motion.div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-2 text-white mb-6">
                Passons à l'action ensemble
              </h2>
              <p className="text-xl text-white/80 mb-10">
                La politique utile commence par le réel — et se termine par des résultats.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/propositions" 
                  className="btn-secondary"
                >
                  Voir nos propositions
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/rejoindre" 
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  Rejoindre l'ADA
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApprochePage;
